// [목적] TanStack Query queryKey factory — 키 일원화 + invalidation 누락 차단
// [이유] fe-screens-skeleton ADR-2 — 컴포넌트가 raw 배열 키 사용 금지.
//         fe-be-wire-up Wave 0 INFRA-07 — user·collection·folioSocial 도메인 queryKey 확장.
//         fe-be-gap-tier1 T7(ADR-TR-4): user.liked·user.forked 키 추가.
// [사이드 이팩트] 본 factory의 키 변경 시 모든 useQuery/invalidateQueries 자동 추적
// [수정 시 고려사항] queryKey 변경은 캐시 무효화로 이어짐 — 운영 중 변경은 신중.
//                   배열 구조는 TanStack Query 네임스페이스 prefix 패턴 유지 — ['domain', 'action', ...args]
//                   [G1-6, 2026-09-17] plan 네임스페이스 제거 — 참조하던 usePlanSession/usePlanJob/
//                   usePlanSlotFilling 이 죽은 /plan API 와 함께 전량 삭제되어 더 이상 참조처가 없음.

import type {
  ListFoliosQuery,
  CollectionListQuery,
  CommentListQuery,
  FestivalCommentListQuery,
  FollowListQuery,
} from '@gangwoncanvas/types';

export const queryKeys = {
  folio: (id: string) => ['folio', id] as const,
  folioList: (params: Partial<ListFoliosQuery> = {}) => ['folio-list', params] as const,

  // [목적] auth 네임스페이스 — 인증 상태 쿼리키 사전 등록
  // [이유] oauth-login ADR-3 — AuthProvider/useAuth 가 staleTime=∞ 로 GET /auth/me 를 캐싱하고,
  //        callback·refresh·logout·completeOnboarding 이후 auth.me 를 invalidate 해야 하므로
  //        컴포넌트별 raw 배열 사용 대신 factory 를 통일 (ADR-2 원칙 동일 적용)
  // [사이드 이팩트] Wave 5 의 WC-FE-CLIENT-01 / WC-FE-PROVIDER-01 이 이 키에 의존;
  //        키 구조 변경 시 AuthProvider·useRequireAuth·OAuthCallbackPage 의 invalidateQueries 경로도 함께 수정
  // [수정 시 고려사항] auth.me 의 배열 루트 ['auth', 'me'] 는 auth 네임스페이스 전체 무효화
  //        (queryClient.invalidateQueries({ queryKey: ['auth'] })) 패턴으로 logout 구현 가능 — 구조 변경 금지
  auth: {
    me: () => ['auth', 'me'] as const,
    session: () => ['auth', 'session'] as const,
  },

  // [목적] user 네임스페이스 — 프로필·폴리오·팔로워·팔로잉·배지 쿼리키
  // [이유] INFRA-07 — ProfilePage·MyFoliosPage 의 useQuery 에서 사용.
  //         user.me() 는 GET /users/me (본인 self-view), user.profile(handle) 은 GET /users/:handle (공개)
  // [사이드 이팩트] invalidateQueries({ queryKey: ['user'] }) 로 전체 user 캐시 무효화 가능
  // [수정 시 고려사항] handle 변경은 캐시 키 분리 — 본인 handle 변경 시 user.me() + user.profile(oldHandle) 양쪽 invalidate 필요
  user: {
    me: () => ['user', 'me'] as const,
    profile: (handle: string) => ['user', 'profile', handle] as const,
    folios: (handle: string, q?: Partial<{ cursor: string; limit: number }>) =>
      ['user', 'folios', handle, q ?? {}] as const,
    followers: (handle: string, q?: Partial<FollowListQuery>) =>
      ['user', 'followers', handle, q ?? {}] as const,
    following: (handle: string, q?: Partial<FollowListQuery>) =>
      ['user', 'following', handle, q ?? {}] as const,
    badges: (subject: 'me' | string) => ['user', 'badges', subject] as const,
    // 2026-09-21 badge-catalog: 도감(획득+미획득) 캐시 키 — badges(보유 목록)와 분리
    badgeCatalog: (subject: 'me' | string) => ['user', 'badgeCatalog', subject] as const,
    // [목적] fe-be-gap-tier1 T7(ADR-TR-4): 본인 좋아요·fork 목록 캐시 키
    // [이유] GET /users/me/liked · /users/me/forked — Profile 탭 실데이터 연결.
    //         cursor 포함 시 페이지별 캐시 분리됨(무한스크롤 useInfiniteQuery 패턴과 일치).
    // [사이드 이팩트] invalidateQueries({ queryKey: ['user', 'liked', 'me'] }) 로 탭 갱신 가능.
    // [수정 시 고려사항] cursor 파라미터는 페이지 단위이므로 useInfiniteQuery 시 initialPageParam 기준.
    liked: (subject: 'me' | string) => ['user', 'liked', subject] as const,
    forked: (subject: 'me' | string) => ['user', 'forked', subject] as const,
    // [목적] G2-3(계약 C3): 본인 북마크 목록 캐시 키 — GET /users/me/bookmarks. liked/forked 와 동일 관례.
    bookmarks: (subject: 'me' | string) => ['user', 'bookmarks', subject] as const,
    // [목적] M7 P1: 홈 통계 카드 캐시 키 — GET /users/me/stats.
    // [이유] HomePage 통계 mock 대체(getMyStats). 본인 전용이라 subject 파라미터 불필요.
    stats: () => ['user', 'stats'] as const,
  },

  // [목적] collection 네임스페이스 — 컬렉션 목록·상세·아이템 쿼리키
  // [이유] INFRA-07 — CollectionPage·ExplorePage 의 useQuery 에서 사용.
  //         collection.list(q) 는 쿼리 파라미터 포함으로 필터/페이지 단위 캐시 분리
  // [사이드 이팩트] invalidateQueries({ queryKey: ['collection'] }) 로 전체 collection 캐시 무효화
  // [수정 시 고려사항] collection.items(id, q) 의 q 변경은 별도 캐시 엔트리 생성 — 무한스크롤 누적 패턴 주의
  collection: {
    list: (q?: Partial<CollectionListQuery>) => ['collection', 'list', q ?? {}] as const,
    detail: (id: string) => ['collection', 'detail', id] as const,
    items: (id: string, q?: { cursor?: string; limit?: number }) =>
      ['collection', 'items', id, q ?? {}] as const,
  },

  // [목적] folioSocial 네임스페이스 — 소셜 인터랙션 쿼리키
  // [이유] INFRA-07 — FolioPage 댓글 섹션 useQuery 에서 사용.
  //         comments(folioId, q) 로 folioId + 페이지네이션 파라미터 단위 캐시 분리
  //         fe-folio-social-wire: likeState/bookmarkState 추가 — folioSchema 에 isLiked/isBookmarked
  //         필드가 없어(BE 미제공) 좋아요·북마크 토글의 낙관적 상태를 react-query 캐시 자체를
  //         "클라이언트 전용 상태 저장소"로 활용(useFolioLike/useFolioBookmark). 동일 folioId 를
  //         참조하는 여러 카드/뷰가 캐시를 공유해 좋아요 상태가 화면 간 동기화된다.
  // [사이드 이팩트] 댓글 추가/수정/삭제 후 invalidateQueries({ queryKey: ['folioSocial', 'comments', folioId] }) 호출.
  //                 likeState/bookmarkState 는 BE 응답이 아닌 세션 로컬 낙관적 상태 — 새로고침 시 liked=false 로 리셋됨
  //                 (BE GET /folios/:id 가 viewer 기준 isLiked/isBookmarked 를 제공하기 전까지 불가피한 제약).
  // [수정 시 고려사항] BE 가 isLiked/isBookmarked 를 folioSchema 에 추가하면 likeState/bookmarkState 초기값을
  //                   그 필드로 대체하고 새로고침 후에도 정확한 상태를 표시하도록 훅을 갱신할 것.
  folioSocial: {
    comments: (folioId: string, q?: Partial<CommentListQuery>) =>
      ['folioSocial', 'comments', folioId, q ?? {}] as const,
    likeState: (folioId: string) => ['folioSocial', 'likeState', folioId] as const,
    bookmarkState: (folioId: string) => ['folioSocial', 'bookmarkState', folioId] as const,
  },

  // [목적] poi 네임스페이스 — 혼잡도 예측 / 연관 POI 쿼리키
  // [이유] WC-TOURAPI-QR-5-04 — usePoiCongestion / usePoiRelated 훅이 단일 factory 경유하도록 등록.
  //         poi(id) prefix 로 invalidateQueries({ queryKey: ['poi'] }) 시 전체 POI 캐시 무효화 가능
  // [사이드 이팩트] 키 구조 변경 시 usePoiCongestion / usePoiRelated 훅의 queryKey 동기화 필수
  // [수정 시 고려사항] days/n 파라미터를 키에 포함해 서로 다른 인수 값은 독립 캐시 엔트리로 분리
  poi: {
    congestion: (poiId: string, days = 30) => ['poi', 'congestion', poiId, days] as const,
    related: (poiId: string, n = 20) => ['poi', 'related', poiId, n] as const,
  },

  // [목적] notification 네임스페이스 — 알림 목록·미읽 카운트 쿼리키
  // [이유] fe-be-gap-tier3 T8(ADR-TR3-6) — useNotifications(탭별 무한스크롤) /
  //         useUnreadCount 가 단일 factory 경유하도록 등록.
  //         notification.list(tab) 은 탭 단위 캐시 분리 — 탭 전환 시 독립 엔트리.
  //         notification.unread() 는 Sidebar 뱃지 전용 쿼리 — refetchOnWindowFocus 패턴(ADR-T3-6).
  // [사이드 이팩트] markRead / markAllRead 성공 후 notification.list(tab) + notification.unread() 양쪽 invalidate.
  // [수정 시 고려사항] tab 종류(all/social/system) 추가 시 ADR-T3-2 동기 갱신.
  notification: {
    list: (tab?: string) => ['notification', 'list', tab ?? 'all'] as const,
    unread: () => ['notification', 'unread'] as const,
  },

  // [목적] rag 네임스페이스 — openui-oneshot-pdf-folio 1화면 흐름 생성 잡 상태 폴링 키
  // [이유] T9(useFolioGeneration) 의 useQuery(GET /rag/conversations/:id/generation) 가 단일 factory 경유.
  //        [FlowLine 83963a6b, 2026-09-18] concepts() 추가 — useConcepts(GET /rag/concepts) 가 단일
  //        factory 경유하도록 등록(ADR-2 원칙: raw 배열 키 금지).
  // [사이드 이팩트] 없음 — 조회 전용, invalidation 은 폴링 자체가 대체(별도 invalidateQueries 호출 없음).
  // [수정 시 고려사항] convId 단위로 캐시 분리 — 동일 convId 재방문 시 마지막 폴링 결과가 즉시 노출될 수 있음(staleTime=0 이므로 재조회 트리거).
  //                   concepts() 는 인자 없는 단일 캐시 엔트리(서버 결정적 상수 — 필터 파라미터 불요).
  rag: {
    generation: (convId: string) => ['rag', 'generation', convId] as const,
    concepts: () => ['rag', 'concepts'] as const,
  },

  // [목적] festivals 네임스페이스 — 홈 "강원 축제" 섹션 목록 조회 캐시 키
  // [이유] fe-home-festivals-live — useFestivals(useQuery) 가 단일 factory 경유하도록 등록.
  //        limit 을 키에 포함해 다른 limit 요청은 독립 캐시 엔트리로 분리.
  // [사이드 이팩트] invalidateQueries({ queryKey: ['festivals'] }) 로 전체 festivals 캐시 무효화 가능(현재 호출부 없음).
  // [수정 시 고려사항] 조회 전용 — mutation 없음. 서버 정렬(ongoing→upcoming→ended)이 캐시 데이터 순서의 SoT.
  festivals: {
    list: (limit = 12) => ['festivals', 'list', limit] as const,
    // 상세: GET /festivals/:id — 목록 캐시와 분리된 id 단위 엔트리(목록 limit 과 무관하게 조회)
    detail: (id: string) => ['festivals', 'detail', id] as const,
  },

  // [목적] festivalComments 네임스페이스 — 축제 상세(/festivals/:id) 댓글 목록 캐시 키
  // [이유] festival-comments Spec OQ-5 — `festivals` 하위에 두면 invalidateQueries({ queryKey: ['festivals'] })
  //        가 축제 목록·상세 캐시까지 함께 날리므로 신규 최상위 네임스페이스로 분리한다(folioSocial 과 동일 관례).
  // [사이드 이팩트] 댓글 작성/수정/삭제 성공 시 useComments 가 이 prefix 로 invalidate → 축제 목록/상세 캐시는 영향 없음.
  // [수정 시 고려사항] contentId 는 TourAPI 숫자 문자열(text). q 는 folioSocial.comments 와 같이 {} 기본값을 포함해
  //                   키 직렬화를 안정시킨다.
  festivalComments: {
    list: (contentId: string, q?: Partial<FestivalCommentListQuery>) =>
      ['festivalComments', 'list', contentId, q ?? {}] as const,
  },
};
